import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { loginReset } from "redux/slice/authentication";
import { leadLoaded } from "redux/slice/leads";
import { arrayToById } from "redux/normalize";
import {
	LeadProfileSuggestionResource,
	LeadProfileSuggestionReferenceResource,
} from "api/resource/lead-profile-suggestion";
import {
	LeadSearchProfileResource,
	LeadConversationDayResource,
	LeadConversationMessageResource,
} from "api/resource/search-agent";

export interface SearchAgentState {
	loading?: boolean;
	error?: string | null;
	/**
	 * Absent until fetched. The API returns a resource with empty lists for a lead with no
	 * generated profile, and null only when the lead is not accessible — so an entry that
	 * exists always means "loaded", whether or not there is anything in it.
	 */
	profileByLeadId: {
		[key: number]: LeadSearchProfileResource | null;
	};
	conversationDaysByLeadId: {
		[key: number]: LeadConversationDayResource[];
	};
	/**
	 * A day's transcript, keyed by lead and then by the day's `YYYY-MM-DD` date. Absent until
	 * fetched, which is what the transcript renders its loading state from — a response that
	 * lands after the reader has moved to another day writes to its own date and is ignored,
	 * so no in-flight bookkeeping is needed to keep a stale day off the screen.
	 */
	messagesByLeadIdAndDate: {
		[key: number]: { [date: string]: LeadConversationMessageResource[] };
	};
	/** Kept apart from `loading` so opening a transcript does not blank the cards behind it. */
	messagesLoading?: boolean;
	/**
	 * Pending profile suggestions, keyed by lead and then by field — a suggestion has no id of
	 * its own, and a lead has at most one per field. Absent until fetched. Fetching, accepting and dismissing never touch `loading`, since the lead page
	 * fetches these for the tab badge while the tab's own cards may be loading.
	 */
	profileSuggestionsByLeadId: {
		[key: number]: { [field: string]: LeadProfileSuggestionResource };
	};
};

const initialState: SearchAgentState = {
	profileByLeadId: {},
	conversationDaysByLeadId: {},
	messagesByLeadIdAndDate: {},
	profileSuggestionsByLeadId: {},
};

const fetchLeadSearchProfile = createAsyncThunk(
	"searchAgent/fetchLeadSearchProfile",
	async (payload: { leadId: number }, thunkAPI) => {
		thunkAPI.dispatch(searchAgentLoading());
		const result = api.getLeadSearchProfile(payload.leadId);
		result
			.then((profile) => thunkAPI.dispatch(searchAgentProfileLoaded({ leadId: payload.leadId, profile })))
			.catch((error) => thunkAPI.dispatch(searchAgentFailed(error)));
		return result;
	}
);

/**
 * Deletes the lead's generated search profile. A failure rejects the thunk rather than
 * setting the slice's error, so the caller can report it without disturbing the cards.
 */
const deleteLeadSearchProfileHistory = createAsyncThunk(
	"searchAgent/deleteLeadSearchProfileHistory",
	async (payload: { leadId: number }) => {
		await api.deleteLeadSearchProfileHistory(payload.leadId);
		return payload;
	}
);

const fetchLeadConversationHistory = createAsyncThunk(
	"searchAgent/fetchLeadConversationHistory",
	async (payload: { leadId: number }, thunkAPI) => {
		thunkAPI.dispatch(searchAgentLoading());
		const result = api.getLeadConversationHistory(payload.leadId);
		result
			.then((days) => thunkAPI.dispatch(searchAgentConversationDaysLoaded({ leadId: payload.leadId, days })))
			.catch((error) => thunkAPI.dispatch(searchAgentFailed(error)));
		return result;
	}
);

const fetchLeadConversationHistoryDay = createAsyncThunk(
	"searchAgent/fetchLeadConversationHistoryDay",
	async (payload: { leadId: number, date: string }, thunkAPI) => {
		thunkAPI.dispatch(searchAgentMessagesLoading());
		const result = api.getLeadConversationHistoryDay(payload.leadId, payload.date);
		result
			.then((messages) => thunkAPI.dispatch(searchAgentMessagesLoaded({
				leadId: payload.leadId,
				date: payload.date,
				messages,
			})))
			.catch((error) => thunkAPI.dispatch(searchAgentFailed(error)));
		return result;
	}
);

/**
 * A failure rejects the thunk and leaves the last fetched suggestions in place, so a transient
 * error does not empty the card or the badge.
 */
const fetchLeadProfileSuggestions = createAsyncThunk(
	"searchAgent/fetchLeadProfileSuggestions",
	async (payload: { leadId: number }) => {
		const suggestions = await api.getLeadProfileSuggestions(payload.leadId);
		return { leadId: payload.leadId, suggestions: suggestions || [] };
	}
);

/**
 * Applies the suggestions server-side and puts the lead it returns into the leads slice, so
 * Lead Details shows the accepted values without a refetch. A failure rejects the thunk and
 * leaves the suggestions in place for the caller to report.
 */
const acceptLeadProfileSuggestions = createAsyncThunk(
	"searchAgent/acceptLeadProfileSuggestions",
	async (payload: { leadId: number, suggestions: LeadProfileSuggestionReferenceResource[] }, thunkAPI) => {
		const lead = await api.acceptLeadProfileSuggestions(payload.leadId, payload.suggestions);
		thunkAPI.dispatch(leadLoaded(lead));
		return payload;
	}
);

/** A failure rejects the thunk and leaves the suggestions in place for the caller to report. */
const dismissLeadProfileSuggestions = createAsyncThunk(
	"searchAgent/dismissLeadProfileSuggestions",
	async (payload: { leadId: number, suggestions: LeadProfileSuggestionReferenceResource[] }) => {
		await api.dismissLeadProfileSuggestions(payload.leadId, payload.suggestions);
		return payload;
	}
);

const removeProfileSuggestions = (
	state: SearchAgentState,
	payload: { leadId: number, suggestions: LeadProfileSuggestionReferenceResource[] }
) => {
	const { leadId, suggestions } = payload;
	const remaining = Object.values(state.profileSuggestionsByLeadId[leadId] || {})
		.filter((suggestion) => !suggestions.some((reference) =>
			reference.field === suggestion.field && reference.suggestedOn === suggestion.suggestedOn));
	state.profileSuggestionsByLeadId = {
		...state.profileSuggestionsByLeadId,
		[leadId]: arrayToById(remaining, "field"),
	};
};

export const searchAgentSlice = createSlice({
	name: "searchAgent",
	initialState,
	reducers: {
		searchAgentLoading: (state) => {
			state.loading = true;
		},
		searchAgentProfileLoaded: (
			state,
			action: PayloadAction<{ leadId: number, profile: LeadSearchProfileResource | null }>
		) => {
			state.loading = false;
			state.error = null;
			state.profileByLeadId = {
				...state.profileByLeadId,
				[action.payload.leadId]: action.payload.profile,
			};
		},
		searchAgentConversationDaysLoaded: (
			state,
			action: PayloadAction<{ leadId: number, days: LeadConversationDayResource[] | null }>
		) => {
			state.loading = false;
			state.error = null;
			state.conversationDaysByLeadId = {
				...state.conversationDaysByLeadId,
				[action.payload.leadId]: action.payload.days || [],
			};
		},
		searchAgentMessagesLoading: (state) => {
			state.messagesLoading = true;
		},
		searchAgentMessagesLoaded: (
			state,
			action: PayloadAction<{
				leadId: number,
				date: string,
				messages: LeadConversationMessageResource[] | null,
			}>
		) => {
			state.messagesLoading = false;
			state.error = null;
			state.messagesByLeadIdAndDate = {
				...state.messagesByLeadIdAndDate,
				[action.payload.leadId]: {
					...state.messagesByLeadIdAndDate[action.payload.leadId],
					[action.payload.date]: action.payload.messages || [],
				},
			};
		},
		searchAgentFailed: (state, action: PayloadAction<Error>) => {
			state.loading = false;
			state.messagesLoading = false;
			state.error = action.payload.message;
		},
		default: (state) => {
			return state;
		},
	},
	extraReducers(builder) {
		// Replaced with the same empty resource the API returns for a lead with no generated
		// profile, so the card shows its empty state without a refetch. Conversation history
		// is not touched: the delete keeps it.
		builder.addCase(deleteLeadSearchProfileHistory.fulfilled, (state, action) => {
			const { leadId } = action.payload;
			state.profileByLeadId = {
				...state.profileByLeadId,
				[leadId]: {
					leadId,
					locations: [],
					mustHaves: [],
					niceToHaves: [],
					dealBreakers: [],
					propertyTypes: [],
				},
			};
		});
		builder.addCase(fetchLeadProfileSuggestions.fulfilled, (state, action) => {
			const { leadId, suggestions } = action.payload;
			state.profileSuggestionsByLeadId = {
				...state.profileSuggestionsByLeadId,
				[leadId]: arrayToById(suggestions, "field"),
			};
		});
		builder.addCase(acceptLeadProfileSuggestions.fulfilled, (state, action) => {
			removeProfileSuggestions(state, action.payload);
		});
		builder.addCase(dismissLeadProfileSuggestions.fulfilled, (state, action) => {
			removeProfileSuggestions(state, action.payload);
		});
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	searchAgentLoading,
	searchAgentProfileLoaded,
	searchAgentConversationDaysLoaded,
	searchAgentMessagesLoading,
	searchAgentMessagesLoaded,
	searchAgentFailed,
} = searchAgentSlice.actions;
export {
	fetchLeadSearchProfile,
	deleteLeadSearchProfileHistory,
	fetchLeadConversationHistory,
	fetchLeadConversationHistoryDay,
	fetchLeadProfileSuggestions,
	acceptLeadProfileSuggestions,
	dismissLeadProfileSuggestions,
};
